import React, { memo, ReactElement, useState } from 'react'

import { TripCard } from '@components/CompactTripCard'
import JourneyCardLayout, { JourneyCardLayoutProps } from '@components/JourneyCard/Layout'
import SeatsModal, { ResultsPageSeatsForm } from '@components/JourneyCard/SeatsModal'
import passengerUtils from '@lib/passengers'
import { useSettings } from '@queries/settings'
import { useParams } from '@stores/params'

export type ConnectionSelectionData = Partial<ResultsPageSeatsForm> & {
  connection: Connection
  fareClassCode: string
}

interface JourneyCardProps extends Omit<JourneyCardLayoutProps, 'onSelect'> {
  seatsRequirement?: number
  tripDirection: Seat.TripDirection
  onSelect: (data: ConnectionSelectionData) => void
  onSeatsSelectionOpen?: (data: ConnectionSelectionData) => void
}

const JourneyCard = ({
  onSelect,
  onSeatsSelectionOpen,
  seatsRequirement,
  tripDirection,
  ...cardProps
}: JourneyCardProps): ReactElement => {
  const { connection, fareClassFilter } = cardProps
  const [{ passengers, pax }] = useParams()
  const [{ seatSelection, journeyCard }] = useSettings()

  const [seatsModalOpened, setSeatsModalOpened] = useState<boolean>(false)
  const [selectedFareClass, setSelectedFareClass] = useState<string>(connection.cheapestFareClassCode)

  const openSeatSelection = (fareClass: string) => {
    setSelectedFareClass(fareClass)
    setSeatsModalOpened(true)
    onSeatsSelectionOpen?.({ connection, fareClassCode: fareClass })
  }

  const selectFareClass = (fareClass: string): void => {
    if (seatSelection.enabledOnSearchResults) {
      openSeatSelection(fareClass)
    } else {
      onSelect({ connection, fareClassCode: fareClass })
    }
  }
  const selectSeats = (seatsData: ResultsPageSeatsForm): void => {
    const { seats } = seatsData
    const [seat] = seats[0] ?? []

    onSelect({ connection, fareClassCode: seat?.fareClass ?? selectedFareClass, ...seatsData })
    setSeatsModalOpened(false)
  }

  return (
    <>
      {seatsModalOpened && (
        <SeatsModal
          onClose={() => setSeatsModalOpened(false)}
          connection={connection}
          tripDirection={tripDirection}
          onSubmit={selectSeats}
          fareFilter={fareClassFilter}
          initialFare={selectedFareClass}
          onFareChange={setSelectedFareClass}
          pax={passengers ? passengerUtils.getPaxCount(passengers) : pax}
          outboundSeatsCount={seatsRequirement}
        />
      )}
      {journeyCard.view === 'detailed' && <JourneyCardLayout onSelect={selectFareClass} {...cardProps} />}
      {journeyCard.view === 'compact' && <TripCard onSelect={selectFareClass} {...cardProps} />}
    </>
  )
}

export default memo(JourneyCard)
